import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import StickyPageHeader from '../components/common/StickyPageHeader';
import {
  Plus,
  Edit2,
  Trash2,
  ArrowDownLeft,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  CreditCard,
  ChevronRight,
  AlertTriangle,
  CheckSquare,
  Bell,
  X
} from 'lucide-react';
import { useToast } from '../context/ToastContext';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  useReceivablesQuery,
  useReceivablesSummaryQuery,
  useCancelReceivable,
  useDeleteReceivable,
  useSendReceivableReminder,
  useSettleReceivable
} from '../hooks/useQueries';
import ConfirmModal from '../components/common/ConfirmModal';
import { ReceivablesSkeleton } from '../components/common/Skeleton';
import useModalBack from '../hooks/useModalBack';

const AVATAR_COLORS = [
  'bg-violet-500', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500',
  'bg-blue-500', 'bg-rose-500', 'bg-teal-500', 'bg-orange-500'
];

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getAvatarColor = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  partial: { label: 'Partial', color: 'bg-blue-100 text-blue-700', icon: DollarSign },
  settled: { label: 'Settled', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-surface-100 text-surface-600', icon: XCircle }
};

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'partial', label: 'Partial' },
  { key: 'settled', label: 'Settled' }
];

export default function ReceivablesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { formatAmount, formatDate, config } = useConfig();
  const { hasEncryptionKey } = useAuth();
  const { isModernUI } = useTheme();

  const [statusFilter, setStatusFilter] = useState('all');
  const [cancelConfirm, setCancelConfirm] = useState({ open: false, receivable: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, receivable: null });
  const [selectedReceivable, setSelectedReceivable] = useState(null);
  const [settleConfirm, setSettleConfirm] = useState({ open: false, receivable: null });

  useModalBack(!!selectedReceivable, () => setSelectedReceivable(null), 'ReceivableDetail');

  // Build query params
  const queryParams = {};
  if (statusFilter !== 'all') {
    queryParams.status = statusFilter;
  }

  // React Query hooks
  const { data: receivablesData = {}, isLoading } = useReceivablesQuery(queryParams, {
    enabled: hasEncryptionKey
  });
  const { data: summaryData = {} } = useReceivablesSummaryQuery({
    enabled: hasEncryptionKey
  });
  const cancelMutation = useCancelReceivable();
  const deleteMutation = useDeleteReceivable();
  const sendReminderMutation = useSendReceivableReminder();
  const settleMutation = useSettleReceivable();

  const receivables = receivablesData.receivables || [];
  const overdueCount = receivables.filter(r => r.dueDate && new Date(r.dueDate) < new Date() && r.status !== 'settled' && r.status !== 'cancelled').length;

  const handleAddNew = () => {
    navigate('/receivables/new');
  };

  const handleEdit = (receivable) => {
    navigate(`/receivables/${receivable._id}/edit`);
  };

  const handleView = (receivable) => {
    if (window.innerWidth < 1280) {
      setSelectedReceivable(receivable);
    } else {
      navigate(`/receivables/${receivable._id}`);
    }
  };

  const handleRecordPayment = (receivable) => {
    navigate(`/receivables/${receivable._id}/payment`);
  };

  const handleCancelClick = (receivable) => {
    setCancelConfirm({ open: true, receivable });
  };

  const handleCancelConfirm = async () => {
    if (cancelConfirm.receivable) {
      try {
        await cancelMutation.mutateAsync(cancelConfirm.receivable._id);
        toast.success('Receivable cancelled');
      } catch (error) {
        toast.error(error.error || 'Failed to cancel receivable');
      }
    }
    setCancelConfirm({ open: false, receivable: null });
  };

  const handleDeleteClick = (receivable) => {
    setDeleteConfirm({ open: true, receivable });
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.receivable) {
      try {
        await deleteMutation.mutateAsync(deleteConfirm.receivable._id);
        toast.success('Receivable permanently deleted');
      } catch (error) {
        toast.error(error.error || 'Failed to delete receivable');
      }
    }
    setDeleteConfirm({ open: false, receivable: null });
  };

  const handleSendReminder = async (receivable) => {
    try {
      await sendReminderMutation.mutateAsync(receivable._id);
      toast.success('Reminder sent successfully');
    } catch (error) {
      toast.error(error.error || 'Failed to send reminder');
    }
  };

  const handleSettleClick = (receivable) => {
    setSettleConfirm({ open: true, receivable });
  };

  const handleSettleConfirm = async () => {
    if (settleConfirm.receivable) {
      try {
        await settleMutation.mutateAsync({ id: settleConfirm.receivable._id, data: {} });
        toast.success('Receivable marked as settled');
      } catch (error) {
        toast.error(error.error || 'Failed to settle receivable');
      }
    }
    setSettleConfirm({ open: false, receivable: null });
  };

  // Calculate remaining amount helper
  const getRemainingAmount = (r) => {
    return (r.amount || 0) - (r.paidAmount || 0);
  };

  // Show skeleton on initial load
  if (isLoading && receivables.length === 0) {
    return <ReceivablesSkeleton />;
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <StickyPageHeader className="xl:hidden">
        <h1 className="text-lg font-semibold text-surface-900">Receivables</h1>
        <p className="text-xs text-surface-500">Track money others owe you</p>
      </StickyPageHeader>

      {/* Mobile Summary */}
      <div className="xl:hidden grid grid-cols-4 gap-2">
        <div className="bg-white rounded-xl border border-surface-200 p-2.5 text-center">
          <p className="text-lg font-bold text-surface-900">{summaryData.pendingCount || 0}</p>
          <p className="text-[10px] text-surface-400 font-medium">Pending</p>
        </div>
        <div className="bg-white rounded-xl border border-surface-200 p-2.5 text-center">
          <p className="text-lg font-bold text-surface-900">{summaryData.partialCount || 0}</p>
          <p className="text-[10px] text-surface-400 font-medium">Partial</p>
        </div>
        <div className="bg-white rounded-xl border border-surface-200 p-2.5 text-center">
          <p className="text-lg font-bold text-surface-900">{summaryData.settledCount || 0}</p>
          <p className="text-[10px] text-surface-400 font-medium">Settled</p>
        </div>
        <div className="bg-white rounded-xl border border-surface-200 p-2.5 text-center">
          <p className="text-sm font-bold text-surface-900" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatAmount(summaryData.totalReceived || 0)}</p>
          <p className="text-[10px] text-surface-400 font-medium">Received</p>
        </div>
      </div>

      {/* Mobile Outstanding + Overdue */}
      {(summaryData.totalPending > 0 || overdueCount > 0) && (
        <div className="xl:hidden flex items-center justify-between">
          <div>
            <p className="text-[11px] text-surface-400">Outstanding</p>
            <p className="text-xl font-bold" style={{ color: config.incomeColor, fontVariantNumeric: 'tabular-nums' }}>{formatAmount(summaryData.totalPending || 0)}</p>
          </div>
          {overdueCount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-amber-50">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
              <span className="text-xs text-amber-600 font-semibold">{overdueCount} overdue</span>
            </div>
          )}
        </div>
      )}

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div className="page-header mb-0">
          <h1 className="page-title">Receivables</h1>
          <p className="page-subtitle">
            Track money that others owe you
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="inline-flex items-center justify-center px-5 py-3 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all"
          style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Receivable
        </button>
      </div>

      {/* Desktop Summary Cards */}
      <div className="hidden xl:grid xl:grid-cols-5 gap-3">
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-400 mb-1">Total Outstanding</p>
          <p className="text-xl font-extrabold" style={{ color: config.incomeColor }}>{formatAmount(summaryData.totalPending || 0)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-400 mb-1">Total Received</p>
          <p className="text-xl font-extrabold text-green-600">{formatAmount(summaryData.totalReceived || 0)}</p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-400 mb-1">Pending / Partial</p>
          <p className="text-xl font-extrabold text-surface-900">{summaryData.pendingCount || 0} <span className="text-surface-300">/</span> {summaryData.partialCount || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-400 mb-1">Settled</p>
          <p className="text-xl font-extrabold text-green-600">{summaryData.settledCount || 0}</p>
        </div>
        <div className="bg-white rounded-2xl border border-surface-200 p-4">
          <p className="text-xs text-surface-400 mb-1">Overdue</p>
          <p className={`text-xl font-extrabold ${overdueCount > 0 ? 'text-amber-500' : 'text-surface-300'}`}>
            {overdueCount}
          </p>
          {overdueCount > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <AlertTriangle className="w-3 h-3 text-amber-500" />
              <span className="text-[11px] text-amber-500 font-medium">Needs attention</span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile: Underline Tabs inside white rounded card */}
      <div className="xl:hidden bg-white rounded-xl border border-surface-200 flex overflow-hidden">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`flex-1 py-3 text-sm font-medium transition-colors border-b-[2.5px] ${
              statusFilter === tab.key
                ? 'text-surface-900'
                : 'text-surface-400 border-transparent'
            }`}
            style={statusFilter === tab.key ? { borderColor: 'var(--accent-bg)', color: 'var(--accent-bg)' } : {}}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Desktop: Pill Tabs */}
      <div className="hidden xl:flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
              statusFilter === tab.key
                ? 'bg-primary-100 text-primary-700'
                : 'bg-surface-100 text-surface-600 hover:bg-surface-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Empty State */}
      {!isLoading && receivables.length === 0 ? (
        <div className="card text-center py-12">
          <div className="w-16 h-16 bg-surface-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <ArrowDownLeft className="w-8 h-8 text-surface-400" />
          </div>
          <h3 className="text-lg font-medium text-surface-900 mb-2">
            No receivables found
          </h3>
          <p className="text-surface-500 mb-4">
            {statusFilter !== 'all'
              ? `No ${statusFilter} receivables. Try changing the filter.`
              : 'Start tracking money that others owe you'
            }
          </p>
          {statusFilter === 'all' && (
            <button
              onClick={handleAddNew}
              className="btn-primary inline-flex"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Receivable
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden xl:block card overflow-hidden p-0">
            <table className="w-full">
              <thead>
                <tr className="table-header border-b border-surface-200">
                  <th className="table-cell">Contact</th>
                  <th className="table-cell">Description</th>
                  <th className="table-cell w-28">Lent Date</th>
                  <th className="table-cell w-28">Due Date</th>
                  <th className="table-cell w-32 text-right">Amount</th>
                  <th className="table-cell w-32 text-right">Remaining</th>
                  <th className="table-cell w-24">Status</th>
                  <th className="table-cell text-right w-36">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                {receivables.map((receivable) => {
                  const StatusIcon = STATUS_CONFIG[receivable.status]?.icon || Clock;
                  const remaining = getRemainingAmount(receivable);
                  const isOverdue = receivable.dueDate && new Date(receivable.dueDate) < new Date() && receivable.status !== 'settled';
                  return (
                    <tr
                      key={receivable._id}
                      className={`hover:bg-surface-50 transition-colors cursor-pointer ${receivable.status === 'cancelled' ? 'opacity-60' : ''}`}
                      onClick={() => handleView(receivable)}
                    >
                      <td className="table-cell">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(receivable.contact?.name)}`}>
                            {getInitials(receivable.contact?.name)}
                          </div>
                          <span className="font-medium text-surface-900">
                            {receivable.contact?.name || 'Unknown'}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="text-surface-700 line-clamp-1">
                          {receivable.description}
                        </span>
                      </td>
                      <td className="table-cell text-surface-600">
                        {formatDate(receivable.lentDate)}
                      </td>
                      <td className="table-cell">
                        <span className={isOverdue ? 'text-red-600 font-medium' : 'text-surface-600'}>
                          {receivable.dueDate ? formatDate(receivable.dueDate) : '-'}
                        </span>
                      </td>
                      <td className="table-cell text-right font-medium text-surface-900">
                        {formatAmount(receivable.amount || 0)}
                      </td>
                      <td className="table-cell text-right">
                        <div className="inline-flex flex-col items-end gap-1">
                          <span className={`font-semibold ${remaining === 0 ? 'text-surface-400' : ''}`} style={remaining > 0 ? { color: config.incomeColor } : {}}>
                            {formatAmount(remaining)}
                          </span>
                          {receivable.status === 'partial' && receivable.amount > 0 && (
                            <div className="w-16 h-1.5 bg-surface-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${Math.round(((receivable.paidAmount || 0) / receivable.amount) * 100)}%` }} />
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${STATUS_CONFIG[receivable.status]?.color}`}>
                          <StatusIcon className="w-3 h-3" />
                          {STATUS_CONFIG[receivable.status]?.label}
                        </span>
                      </td>
                      <td className="table-cell text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          {receivable.status !== 'settled' && receivable.status !== 'cancelled' && (
                            <>
                              <button
                                onClick={() => handleRecordPayment(receivable)}
                                className="p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                title="Record Payment"
                              >
                                <CreditCard className="w-4 h-4" />
                              </button>
                              {receivable.contact?.email && (
                                <button
                                  onClick={() => handleSendReminder(receivable)}
                                  className="p-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Send Reminder"
                                  disabled={sendReminderMutation.isPending}
                                >
                                  <Send className="w-4 h-4" />
                                </button>
                              )}
                            </>
                          )}
                          {receivable.status !== 'cancelled' && (
                            <button
                              onClick={() => handleEdit(receivable)}
                              className="p-2 text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                              title="Edit"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {receivable.status !== 'settled' && receivable.status !== 'cancelled' && (
                            <button
                              onClick={() => handleCancelClick(receivable)}
                              className="p-2 text-expense-500 hover:text-expense-600 hover:bg-expense-50 rounded-lg transition-colors"
                              title="Cancel"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                          {receivable.status === 'cancelled' && (
                            <button
                              onClick={() => handleDeleteClick(receivable)}
                              className="p-2 text-expense-500 hover:text-expense-600 hover:bg-expense-50 rounded-lg transition-colors"
                              title="Delete Permanently"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards View */}
          <div className="xl:hidden space-y-2 pb-20">
            {receivables.map((receivable, index) => {
              const remaining = getRemainingAmount(receivable);
              const isOverdue = receivable.dueDate && new Date(receivable.dueDate) < new Date() && receivable.status !== 'settled' && receivable.status !== 'cancelled';
              const prevIsOverdue = index > 0 && receivables[index-1].dueDate && new Date(receivables[index-1].dueDate) < new Date() && receivables[index-1].status !== 'settled' && receivables[index-1].status !== 'cancelled';
              const showOverdueHeader = isOverdue && (index === 0 || !prevIsOverdue);
              const contactName = receivable.contact?.name || 'Unknown';
              const initials = getInitials(contactName);
              const avatarColor = getAvatarColor(contactName);
              const paidPercent = receivable.amount > 0 ? Math.round(((receivable.paidAmount || 0) / receivable.amount) * 100) : 0;

              return (
                <div key={receivable._id}>
                  {showOverdueHeader && (
                    <div className="flex items-center gap-2 px-1 pt-2 pb-1">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                      <span className="text-sm font-bold text-amber-500">Overdue</span>
                    </div>
                  )}
                  <div
                    className={`bg-white rounded-2xl p-4 border cursor-pointer active:scale-[0.98] transition-all ${
                      isOverdue ? 'border-orange-200 bg-orange-50/30' : 'border-surface-100'
                    } ${receivable.status === 'cancelled' ? 'opacity-60' : ''}`}
                    onClick={() => handleView(receivable)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm ${avatarColor}`}>
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-bold text-surface-900 text-[15px] truncate">{contactName}</p>
                          <p className="font-extrabold text-surface-900 text-base flex-shrink-0">{formatAmount(receivable.amount || 0)}</p>
                        </div>
                        <div className="flex items-center justify-between mt-0.5">
                          <p className="text-xs text-surface-400 truncate">{receivable.description}</p>
                          <span className={`text-[11px] font-semibold ml-2 flex-shrink-0 ${
                            receivable.status === 'settled' ? 'text-green-500' :
                            receivable.status === 'partial' ? 'text-amber-500' :
                            'text-amber-500'
                          }`}>
                            {STATUS_CONFIG[receivable.status]?.label}
                          </span>
                        </div>

                        {/* Progress bar for partial */}
                        {receivable.status === 'partial' && (
                          <div className="mt-2.5">
                            <div className="h-[5px] bg-surface-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-primary-500 transition-all" style={{ width: `${paidPercent}%` }} />
                            </div>
                          </div>
                        )}

                        {/* Bottom row */}
                        <div className="flex items-center justify-between mt-2">
                          {receivable.status === 'settled' ? (
                            <span className="text-xs text-green-500 font-medium flex items-center gap-1">
                              <CheckSquare className="w-3 h-3" />
                              Fully settled{receivable.settledDate ? ` on ${formatDate(receivable.settledDate)}` : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-green-500 font-semibold">
                              Received {formatAmount(receivable.paidAmount || 0)}
                            </span>
                          )}
                          {receivable.status !== 'settled' && (
                            <span className="text-[11px] text-surface-400">
                              Due {formatAmount(remaining)}{receivable.dueDate ? ` \u00b7 ${formatDate(receivable.dueDate)}` : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Mobile Detail Bottom Sheet */}
      {selectedReceivable && (
        <>
          {/* Overlay */}
          <div className="xl:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-[100]" onClick={() => setSelectedReceivable(null)} />

          {/* Bottom Sheet */}
          <div className="xl:hidden fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl z-[101] animate-slide-up" style={{ maxHeight: '85vh' }}>
            {/* Drag Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-9 h-1 bg-surface-300 rounded-full" />
            </div>

            <div className="overflow-y-auto px-5 pb-6" style={{ maxHeight: 'calc(85vh - 40px)' }}>
              {(() => {
                const r = selectedReceivable;
                const contactName = r.contact?.name || 'Unknown';
                const remaining = getRemainingAmount(r);
                const paidPercent = r.amount > 0 ? Math.round(((r.paidAmount || 0) / r.amount) * 100) : 0;

                return (
                  <>
                    {/* Header: Avatar + Name + Status + Edit + Close */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base ${getAvatarColor(contactName)}`}>
                        {getInitials(contactName)}
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-bold text-surface-900">{contactName}</p>
                        <span className={`text-xs font-semibold ${
                          r.status === 'settled' ? 'text-green-500' :
                          r.status === 'partial' ? 'text-amber-500' :
                          'text-amber-500'
                        }`}>
                          {STATUS_CONFIG[r.status]?.label}
                        </span>
                      </div>
                      <button
                        onClick={() => { setSelectedReceivable(null); navigate(`/receivables/${r._id}/edit`); }}
                        className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center"
                      >
                        <Edit2 className="w-4 h-4 text-surface-500" />
                      </button>
                      <button
                        onClick={() => setSelectedReceivable(null)}
                        className="w-9 h-9 rounded-xl bg-surface-100 flex items-center justify-center"
                      >
                        <X className="w-4 h-4 text-surface-500" />
                      </button>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-surface-400 mb-1">{r.description}</p>

                    {/* Amount */}
                    <p className="text-3xl font-black text-surface-900 tracking-tight mb-1">{formatAmount(r.amount || 0)}</p>

                    {/* Dates */}
                    <p className="text-xs text-surface-400 mb-5">
                      Lent on {formatDate(r.lentDate)}{r.dueDate ? ` \u00b7 Due ${formatDate(r.dueDate)}` : ''}
                    </p>

                    {/* Received / Remaining */}
                    <div className="flex justify-between mb-2">
                      <div>
                        <p className="text-[11px] text-surface-400 mb-0.5">Received</p>
                        <p className="text-xl font-extrabold text-green-500">{formatAmount(r.paidAmount || 0)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] text-surface-400 mb-0.5">Remaining</p>
                        <p className="text-xl font-extrabold text-amber-500">{formatAmount(remaining)}</p>
                      </div>
                    </div>

                    {/* Progress */}
                    {r.amount > 0 && (
                      <>
                        <div className="h-[6px] bg-surface-100 rounded-full overflow-hidden mb-1">
                          <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${paidPercent}%` }} />
                        </div>
                        <p className="text-[11px] text-surface-400 text-right mb-5">{paidPercent}% collected</p>
                      </>
                    )}

                    {/* Action Buttons */}
                    {r.status !== 'settled' && r.status !== 'cancelled' && (
                      <div className="space-y-2.5">
                        <div className="grid grid-cols-2 gap-2.5">
                          <button
                            onClick={() => { setSelectedReceivable(null); navigate(`/receivables/${r._id}/payment`); }}
                            className="flex items-center justify-center gap-2 py-3 rounded-xl border border-surface-200 text-sm font-semibold text-surface-700"
                          >
                            <CreditCard className="w-4 h-4" />
                            Record Payment
                          </button>
                          <button
                            onClick={() => { setSelectedReceivable(null); handleSettleClick(r); }}
                            className="flex items-center justify-center gap-2 py-3 rounded-xl border border-surface-200 text-sm font-semibold text-surface-700"
                          >
                            <CheckSquare className="w-4 h-4" />
                            Mark Settled
                          </button>
                        </div>

                        <button
                          onClick={() => { handleSendReminder(r); setSelectedReceivable(null); }}
                          disabled={sendReminderMutation.isPending || !r.contact?.email}
                          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-amber-700 bg-amber-50 disabled:opacity-50"
                        >
                          <Bell className="w-4 h-4" />
                          Send Reminder
                        </button>

                        <button
                          onClick={() => { setSelectedReceivable(null); handleCancelClick(r); }}
                          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-red-500 bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    )}

                    {r.status === 'settled' && (
                      <button
                        onClick={() => { setSelectedReceivable(null); navigate(`/receivables/${r._id}`); }}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-surface-200 text-sm font-semibold text-surface-700"
                      >
                        View Full Details
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </>
      )}

      {/* Mobile FAB */}
      <button onClick={handleAddNew} className="fab" aria-label="Add receivable">
        <Plus className="w-6 h-6" />
      </button>

      {/* Settle Confirmation Modal */}
      <ConfirmModal
        isOpen={settleConfirm.open}
        onClose={() => setSettleConfirm({ open: false, receivable: null })}
        onConfirm={handleSettleConfirm}
        title="Mark as Settled"
        message="Mark this receivable as fully settled? This means you've received the full amount."
        confirmText="Mark Settled"
        variant="primary"
      />

      {/* Cancel Confirmation Modal */}
      <ConfirmModal
        isOpen={cancelConfirm.open}
        onClose={() => setCancelConfirm({ open: false, receivable: null })}
        onConfirm={handleCancelConfirm}
        title="Cancel Receivable"
        message="Are you sure you want to cancel this receivable? This action cannot be undone."
        confirmText="Cancel Receivable"
        variant="danger"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, receivable: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Permanently"
        message="Are you sure you want to permanently delete this receivable? This will also delete all associated payment records. This action cannot be undone."
        confirmText="Delete Permanently"
        variant="danger"
      />
    </div>
  );
}
